import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger.js';
import profileDeleteService from '../utils/profileDeleteService';
import { sendAdminDeletionNotification } from './emailNotificationService';

const adminLogger = createLogger('AdminModerationService');

// Helper function to check if user is admin or moderator
const checkAdminAccess = async () => {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  
  if (authError || !user) {
    throw new Error('Authentication required');
  }

  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  if (profileError || !['admin', 'moderator'].includes(profile?.role)) {
    throw new Error('Admin or moderator access required');
  }

  return user;
};

// Log admin action
const logAdminAction = async (adminId, actionType, targetUserId = null, targetJobId = null, targetReportId = null, reason = null, notes = null) => {
  try {
    const insertData = {
      admin_user_id: adminId,
      action_type: actionType,
      target_user_id: targetUserId || null,
      target_job_id: targetJobId || null,
      target_report_id: targetReportId || null,
      reason: reason || null,
      notes: notes || null
    };
    
    const { error } = await supabase
      .from('admin_actions')
      .insert(insertData);
    
    if (error) {
      adminLogger.error('Failed to insert admin action:', error);
    }
  } catch (error) {
    adminLogger.error('Failed to log admin action', error);
  }
};

export const adminModerationService = {
  // Get moderation statistics
  async getModerationStats() {
    try {
      await checkAdminAccess();

      // Get submitted reports count (equivalent to pending)
      const { data: pendingReports, error: reportsError } = await supabase
        .from('reports')
        .select('id')
        .eq('status', 'submitted');

      if (reportsError) throw reportsError;

      // Get current user statuses by finding the most recent record for each user
      const { data: currentUserStatuses, error: userError } = await supabase
        .from('user_moderation_status')
        .select('user_id, status, created_at')
        .order('created_at', { ascending: false });

      if (userError) throw userError;

      // Get the most recent status for each user
      const userStatusMap = new Map();
      currentUserStatuses?.forEach(record => {
        if (!userStatusMap.has(record.user_id)) {
          userStatusMap.set(record.user_id, record.status);
        }
      });

      // Count suspended users
      const suspendedCount = Array.from(userStatusMap.values()).filter(status => status === 'suspended').length;

      // Get flagged jobs count
      const { data: flaggedJobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id')
        .eq('status', 'flagged');

      if (jobsError) throw jobsError;

      // Get today's admin actions count
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();
      
      const { data: todayActions, error: actionsError } = await supabase
        .from('admin_actions')
        .select('id')
        .gte('created_at', todayISO);

      if (actionsError) throw actionsError;

      return {
        success: true,
        data: {
          pendingReports: pendingReports?.length || 0,
          suspendedUsers: suspendedCount || 0,
          flaggedJobs: flaggedJobs?.length || 0,
          todayActions: todayActions?.length || 0
        }
      };
    } catch (error) {
      console.error('Error getting moderation stats:', error);
      return {
        success: false,
        error: error.message || 'Failed to get moderation statistics'
      };
    }
  },

  // Get all reports
  async getReports() {
    try {
      await checkAdminAccess();

      const { data, error } = await supabase
        .from('reports')
        .select(`
          *,
          reporter:user_profiles!reports_reported_by_fkey(full_name, email),
          reported_user:user_profiles!reports_reported_user_fkey(full_name, email),
          reported_job:jobs(title, id)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      console.error('Error getting reports:', error);
      return {
        success: false,
        error: error.message || 'Failed to get reports'
      };
    }
  },

  // Update report status
  async updateReportStatus(reportId, status, adminNotes = '') {
    try {
      const admin = await checkAdminAccess();

      // Map common action values to correct status values as a safety check
      const statusMapping = {
        'resolve': 'resolved',
        'dismiss': 'dismissed',
        'escalate': 'under_review'
      };
      const mappedStatus = statusMapping[status] || status;

      const { data, error } = await supabase
        .from('reports')
        .update({
          status: mappedStatus,
          admin_notes: adminNotes,
          resolved_at: ['approved', 'resolved', 'dismissed', 'rejected'].includes(mappedStatus) ? new Date().toISOString() : null,
          resolved_by: ['approved', 'resolved', 'dismissed', 'rejected'].includes(mappedStatus) ? admin.id : null
        })
        .eq('id', reportId)
        .select()
        .single();

      if (error) throw error;

      // Log admin action with correct enum value  
      let actionType;
      switch (mappedStatus) {
        case 'resolved':
          actionType = 'report_resolved';
          break;
        case 'dismissed':
          actionType = 'report_dismissed';
          break;
        default:
          actionType = 'report_resolved'; // fallback
      }

      await logAdminAction(
        admin.id,
        actionType,
        null, // target_user_id
        null, // target_job_id  
        reportId, // target_report_id
        `Report status changed to ${status}`, // reason
        adminNotes // notes
      );

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error updating report status:', error);
      return {
        success: false,
        error: error.message || 'Failed to update report status'
      };
    }
  },

  // Get all users with moderation info
  async getUsers() {
    try {
      await checkAdminAccess();

      // Get all users
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, role, is_active, is_verified, profile_picture, created_at, updated_at')
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // Batch fetch moderation statuses restricted to the returned users (prevents full table scan)
      const userIds = (users || []).map(u => u.id);
      const { data: allStatuses, error: statusError } = await supabase
        .from('user_moderation_status')
        .select('user_id, status, expires_at, reason, created_at')
        .in('user_id', userIds.length > 0 ? userIds : ['00000000-0000-0000-0000-000000000000'])
        .order('created_at', { ascending: false });

      if (statusError) throw statusError;
      // Build lookup map: user_id → latest status (first entry per user due to ordering)
      const statusMap = new Map();
      if (allStatuses) {
        for (const s of allStatuses) {
          if (!statusMap.has(s.user_id)) {
            statusMap.set(s.user_id, { status: s.status, expires_at: s.expires_at, reason: s.reason });
          }
        }
      }

      const defaultStatus = { status: 'active', expires_at: null, reason: null };
      const usersWithStatus = (users || []).map(user => ({
        ...user,
        moderation_status: statusMap.get(user.id) || defaultStatus
      }));

      return {
        success: true,
        data: usersWithStatus || []
      };
    } catch (error) {
      console.error('Error getting users:', error);
      return {
        success: false,
        error: error.message || 'Failed to get users'
      };
    }
  },

  // Update user status
  async updateUserStatus(userId, status, reason = '', duration = null) {
    try {
      const admin = await checkAdminAccess();

      let expiresAt = null;
      if (status === 'suspended' && duration) {
        const now = new Date();
        expiresAt = new Date(now.getTime() + (duration * 24 * 60 * 60 * 1000));
      }

      // Fetch user info for email notification
      const { data: targetUser, error: userError } = await supabase
        .from('user_profiles')
        .select('email, full_name')
        .eq('id', userId)
        .single();

      if (userError) {
        console.error('Error fetching user for email:', userError);
      }

      // Upsert user moderation status (with onConflict to update existing record)
      const { data, error } = await supabase
        .from('user_moderation_status')
        .upsert({
          user_id: userId,
          status,
          reason,
          expires_at: expiresAt?.toISOString(),
          moderator_id: admin.id,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select()
        .maybeSingle();

      if (error) {
        console.error('Error upserting moderation status:', error);
        throw error;
      }

      // Update user_profiles.is_active to enforce account status
      const isActive = (status === 'active');
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ is_active: isActive })
        .eq('id', userId);

      if (profileError) {
        console.error('Error updating user profile active status:', profileError);
        // Continue even if profile update fails - moderation status is more important
      }

      console.log(`User ${userId} moderation status updated to ${status}, is_active set to ${isActive}`);

      // Log admin action with correct enum value
      let actionType;
      switch (status) {
        case 'suspended':
          actionType = 'user_suspended';
          break;
        case 'active':
          actionType = 'user_activated';
          break;
        case 'banned':
          actionType = 'user_banned';
          break;
        case 'warned':
          actionType = 'user_warned';
          break;
        default:
          actionType = 'user_activated'; // fallback
      }
      
      await logAdminAction(
        admin.id,
        actionType,
        userId, // target_user_id
        null, // target_job_id
        null, // target_report_id
        reason, // reason
        `Status changed to ${status}. Duration: ${duration || 'N/A'} days` // notes
      );

      // Send email notification to user (non-blocking)
      if (targetUser && targetUser.email) {
        try {
          const emailType = status === 'suspended' ? 'user-suspended' 
            : status === 'warned' ? 'user-warned'
            : status === 'banned' ? 'user-banned'
            : null;

          if (emailType) {
            // Build email body with clean data structure
            const emailBody = {
              emailType,
              email: String(targetUser.email),
              fullName: String(targetUser.full_name || 'User'),
              reason: String(reason || 'Violation of Community Guidelines'),
            };

            // Add status-specific data with type safety
            if (status === 'suspended') {
              emailBody.duration = String(duration ? `${duration} days` : 'Pending review');
              if (expiresAt) {
                try {
                  emailBody.expiresAt = new Date(expiresAt).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                } catch (dateError) {
                  console.warn('Error formatting expiry date:', dateError);
                  emailBody.expiresAt = String(expiresAt);
                }
              }
              emailBody.appealUrl = 'https://dashboard.vasprouts.com/support';
            } else if (status === 'warned') {
              emailBody.violationDetails = 'This warning has been logged in your account history.';
              emailBody.guidelinesUrl = 'https://vasprouts.com/community-guidelines';
            } else if (status === 'banned') {
              emailBody.isPermanent = true;
              emailBody.appealUrl = 'https://dashboard.vasprouts.com/support';
            }

            console.log('Sending email with type:', emailType);
            console.log('Email body:', JSON.stringify(emailBody, null, 2));

            // Validate required fields exist
            if (!emailBody.email || !emailBody.fullName) {
              console.error('Missing required email fields:', { email: emailBody.email, fullName: emailBody.fullName });
              return;
            }

            // Call Edge Function to send email
            const { error: emailError, data: emailData } = await supabase.functions.invoke('send-email', {
              body: emailBody
            });

            if (emailError) {
              console.error(`Failed to send ${emailType} email:`, emailError);
              console.error('Error details:', JSON.stringify(emailError));
              // Don't throw - email failure shouldn't block the status update
            } else {
              console.log(`Successfully sent ${emailType} email to ${targetUser.email}`, emailData);
            }
          }
        } catch (emailError) {
          console.error('Error sending moderation email:', emailError);
          console.error('Error stack:', emailError.stack);
          // Don't throw - email failure shouldn't block the status update
        }
      }

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error updating user status:', error);
      return {
        success: false,
        error: error.message || 'Failed to update user status'
      };
    }
  },

  // Delete user account (admin-initiated)
  async deleteUser(userId, reason = '', adminNote = '') {
    try {
      const admin = await checkAdminAccess();

      // Fetch user info for audit log before deletion
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('email, full_name, role')
        .eq('id', userId)
        .single();

      const userEmail = userProfile?.email || 'unknown';
      const userName = userProfile?.full_name || 'Unknown User';
      const userRole = userProfile?.role || 'user';

      adminLogger.info(`Admin ${admin.id} initiating deletion of user ${userId} (${userName})`);

      // Send notification email to user BEFORE deletion (while user data still exists)
      // Note: Email failure is logged as warning and doesn't block deletion
      const emailResult = await sendAdminDeletionNotification(userEmail, userName, reason, adminNote);
      if (emailResult.warning) {
        adminLogger.warn(`Email notification warning: ${emailResult.warning}`);
      } else if (emailResult.success) {
        adminLogger.info(`Deletion notification sent to ${userEmail}`);
      }

      // Log admin action BEFORE deletion — the FK on admin_actions.target_user_id
      // references user_profiles, so the row must still exist when we insert.
      await logAdminAction(
        admin.id,
        'user_deleted',
        userId,
        null,
        null,
        reason || 'Admin-initiated account deletion',
        `User deleted: ${userName} (${userEmail}, ${userRole}). Admin note: ${adminNote || 'None'}`
      );

      // Call profileDeleteService to perform full account deletion
      // Pass shouldSignOut: false to prevent logging out the admin
      const result = await profileDeleteService.deleteUserAccount(userId, false);

      if (result.success) {
        adminLogger.info(`Successfully deleted user ${userId}`);
        
        return {
          success: true,
          message: `User account deleted successfully`,
          data: {
            deletedUserId: userId,
            deletedUserName: userName,
            deletedUserEmail: userEmail
          }
        };
      } else {
        adminLogger.error(`Failed to delete user ${userId}:`, result.error);
        return {
          success: false,
          error: result.error || 'Failed to delete user account'
        };
      }
    } catch (error) {
      adminLogger.error('Error in deleteUser:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete user account'
      };
    }
  },

  // Get all jobs
  async getJobs() {
    try {
      await checkAdminAccess();

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          employer:user_profiles!jobs_employer_id_fkey(full_name, email, company_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      console.error('Error getting jobs:', error);
      return {
        success: false,
        error: error.message || 'Failed to get jobs'
      };
    }
  },

  // Update job status
  async updateJobStatus(jobId, status, adminNotes = '') {
    try {
      const admin = await checkAdminAccess();

      const { data, error } = await supabase
        .from('jobs')
        .update({
          status,
          admin_notes: adminNotes
        })
        .eq('id', jobId)
        .select()
        .single();

      if (error) throw error;

      // Log admin action with correct enum value
      let actionType;
      switch (status) {
        case 'flagged':
          actionType = 'job_flagged';
          break;
        case 'approved':
          actionType = 'job_approved';
          break;
        case 'removed':
          actionType = 'job_removed';
          break;
        default:
          actionType = 'job_approved'; // fallback
      }

      await logAdminAction(
        admin.id,
        actionType,
        null, // target_user_id
        jobId, // target_job_id
        null, // target_report_id
        `Job status changed to ${status}`, // reason
        adminNotes // notes
      );

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error updating job status:', error);
      return {
        success: false,
        error: error.message || 'Failed to update job status'
      };
    }
  },

  // Get recent admin actions
  async getRecentActions(limit = 50) {
    try {
      await checkAdminAccess();

      const { data, error } = await supabase
        .from('admin_actions')
        .select(`
          id,
          action_type,
          target_user_id,
          target_job_id,
          target_report_id,
          reason,
          notes,
          created_at,
          admin:user_profiles!admin_actions_admin_user_id_fkey(full_name, email)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      console.error('Error getting recent actions:', error);
      return {
        success: false,
        error: error.message || 'Failed to get recent actions'
      };
    }
  },

  // Get audit logs
  async getAuditLogs(filters = {}) {
    try {
      await checkAdminAccess();

      let query = supabase
        .from('audit_logs')
        .select(`
          *,
          user:user_profiles(full_name, email)
        `);

      // Apply filters
      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }
      
      if (filters.action) {
        query = query.eq('action', filters.action);
      }
      
      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      
      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(filters.limit || 100);

      if (error) throw error;

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      console.error('Error getting audit logs:', error);
      return {
        success: false,
        error: error.message || 'Failed to get audit logs'
      };
    }
  },

  // Delete report
  async deleteReport(reportId) {
    try {
      const admin = await checkAdminAccess();

      const { error } = await supabase
        .from('reports')
        .delete()
        .eq('id', reportId);

      if (error) throw error;

      // Log admin action - use content_removed for delete action
      await logAdminAction(
        admin.id,
        'content_removed',
        null, // target_user_id
        null, // target_job_id
        reportId, // target_report_id
        'Report deleted by admin', // reason
        'Report has been permanently removed' // notes
      );

      return {
        success: true
      };
    } catch (error) {
      console.error('Error deleting report:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete report'
      };
    }
  },

  // Bulk update reports
  async bulkUpdateReports(reportIds, status, adminNotes = '') {
    try {
      const admin = await checkAdminAccess();

      // Map common action values to correct status values as a safety check
      const statusMapping = {
        'resolve': 'resolved',
        'dismiss': 'dismissed',
        'escalate': 'under_review'
      };
      const mappedStatus = statusMapping[status] || status;

      const { data, error } = await supabase
        .from('reports')
        .update({
          status: mappedStatus,
          admin_notes: adminNotes,
          resolved_at: ['approved', 'resolved', 'dismissed', 'rejected'].includes(mappedStatus) ? new Date().toISOString() : null,
          resolved_by: ['approved', 'resolved', 'dismissed', 'rejected'].includes(mappedStatus) ? admin.id : null
        })
        .in('id', reportIds)
        .select();

      if (error) throw error;

      // Log admin action for each report with correct enum value
      let actionType;
      switch (mappedStatus) {
        case 'resolved':
          actionType = 'report_resolved';
          break;
        case 'dismissed':
          actionType = 'report_dismissed';
          break;
        default:
          actionType = 'report_resolved'; // fallback
      }

      for (const reportId of reportIds) {
        await logAdminAction(
          admin.id,
          actionType,
          null, // target_user_id
          null, // target_job_id
          reportId, // target_report_id
          `Bulk update: Report status changed to ${status}`, // reason
          adminNotes // notes
        );
      }

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      console.error('Error bulk updating reports:', error);
      return {
        success: false,
        error: error.message || 'Failed to bulk update reports'
      };
    }
  },

  // Add admin user (only callable by admin users)
  async addAdminUser(email) {
    try {
      const admin = await checkAdminAccess();

      // Additional check to ensure only admins (not moderators) can add admin users
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', admin.id)
        .single();

      if (profile?.role !== 'admin') {
        return {
          success: false,
          error: 'Only admin users can add new admin users'
        };
      }

      // Use the database function we created
      const { data, error } = await supabase.rpc('add_admin_user', {
        admin_email: email
      });

      if (error) throw error;

      // If successful, log the admin action
      if (data.success) {
        await logAdminAction(
          admin.id,
          'user_activated', // closest available action type
          null, // target_user_id will be filled when user actually signs up
          null, // target_job_id
          null, // target_report_id
          'Admin user profile created', // reason
          `Created admin profile for email: ${email}` // notes
        );
      }

      return data;
    } catch (error) {
      console.error('Error adding admin user:', error);
      return {
        success: false,
        error: error.message || 'Failed to add admin user'
      };
    }
  },

  // Add moderator user (only callable by admin users)
  async addModeratorUser(email) {
    try {
      const admin = await checkAdminAccess();

      // Additional check to ensure only admins (not moderators) can add moderator users
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', admin.id)
        .single();

      if (profile?.role !== 'admin') {
        return {
          success: false,
          error: 'Only admin users can add new moderator users'
        };
      }

      // Use the database function we created
      const { data, error } = await supabase.rpc('add_moderator_user', {
        moderator_email: email
      });

      if (error) throw error;

      // If successful, log the admin action
      if (data.success) {
        await logAdminAction(
          admin.id,
          'user_activated', // closest available action type
          null, // target_user_id will be filled when user actually signs up
          null, // target_job_id
          null, // target_report_id
          'Moderator user profile created', // reason
          `Created moderator profile for email: ${email}` // notes
        );
      }

      return data;
    } catch (error) {
      console.error('Error adding moderator user:', error);
      return {
        success: false,
        error: error.message || 'Failed to add moderator user'
      };
    }
  }
};

export default adminModerationService;
